
/*
 * store an actual GPS status
 * filter a realtime GPS data to prevent sensor jitter or a short-time accuracy drop
 *
 * return location struct or null if location isn't changed
 */
import {Evented} from "@/engine/evented.js";
import {statCollector} from "@/engine/helpers/statCollector.js";

let gpsDataRecord = {
    lat: null,
    lng: null,
    alt: null,
    acc: null,
    offset: null, //meters
    speed: null, //meters per sec
    ts: null, //timestamp
};

class gpsStateMachine extends Evented {

    /*
        [requiredAccuracy]    - accuracy in meters we decide enough to accept the location as correct
        [timeout]             - GPS waiting timeout
        [initialLocationData] - gpsDataRecord struct
        [outputCallback]      - a callback function to periodic sending filtered data to an external feed
     */
    constructor(options) {
        super()
        this.gpsDataQueue = [];
        this.gpsHighAccuracyDataQueue = [];
        this.CONST_gpsDataQueueLength = 10;             //number of gps records stored in the queue
        this.CONST_gpsDataQueueTimeWindow = 100 * 1000; //sec
        this.CONST_gpsDefaultSendInterval = 10 * 1000;  //sec
        this.CONST_gpsMaxSendInterval     = 100 * 1000; //sec
        this.CONST_dgrInRad = Math.PI / 180;        //redians per 1 degree
        this.CONST_dgrInMeters = 111000;            //1 degree arc length by a meridian
        this.requiredAccuracy = options && options.requiredAccuracy ? options.requiredAccuracy : 50;
        this.timeout = options && options.timeout ? options.timeout : 20000;
        this.state = {
            startupTimestamp: new Date().getTime(),
            lastAcceptedData: null,
            lastSendData: null,
            lastSendTimestamp: null,
            located: false,
            run:     false,
            initialLocatingCompleted: false,
            accuracyTooLow: false,
            accuracy: 0
        };
        this.sendUpdate = options ? options.outputCallback : null;
        this.sendInterval = this.CONST_gpsDefaultSendInterval;
        this.sendUpdateTimerID = null;
        this.map = options ? options.map : null

        //gps state metrics collector
        this.stat = new statCollector(
            "gps",
            {
                gpsUpdates:         0,
                gpsUpdatesFiltered: 0,
                lastGpsUpdateTimestamp:     0,
                lastGpsUpdateAppliedTimestamp:  0,
                gpsAPIErrorEvents: 0,
                lastGpsSendTimestamp:  function () {
                    return this.state.lastSendTimestamp;
                }.bind(this),
                gpsAcceptedData: function () {
                    return this.state.lastAcceptedData
                }.bind(this),
                gpsSendData: function () {
                    return this.state.lastSendData
                }.bind(this),
                gpsList: function () {
                    return this.gpsDataQueue;
                }.bind(this)
            },
            null,
            { ts: "timestamp", speed: "round2", lat: "round6", lng: "round6", acc: "int", alt: "int"}
        );

        //high accuracy locations metrics collector
        this.highAccuracyLocationStat = new statCollector(
            "gps-highAccuracy",
            {
                gpsList: function () {
                    return this.gpsHighAccuracyDataQueue;
                }.bind(this)
            },
            null,
            { ts: "timestamp", speed: "round2", lat: "round6", lng: "round6", acc: "int", alt: "int"}
        );

        /*
             ref table of parallels lengths in meters for various latitudes used for distance calculation
         */
        this.CONST_ParallelLengthsItemsCount = 180;
        this.CONST_dictParallelLengthsStep = 180 / this.CONST_ParallelLengthsItemsCount;
        this.dictParallelLengths = [];

        for( let i = 0; i < this.CONST_ParallelLengthsItemsCount; i++) {
            let curDgr = this.CONST_dictParallelLengthsStep * i;
            this.dictParallelLengths.push(Math.cos(curDgr * this.CONST_dgrInRad) * this.CONST_dgrInMeters);
        }

        if (options && options.initialLocationData) {
            this.state.lastAcceptedData = this.newDataRecord(options.initialLocationData.lat, options.initialLocationData.lng, null, options.initialLocationData.acc, options.initialLocationData.ts);
            this._calcAccuracyAvg([this.state.lastAcceptedData]);
        }

        if (this.map) {
            this.map.on('locationerror', this.GPSLocationErrorEvent.bind(this));
            this.map.on('locationfound', this.GPSLocationFoundEvent.bind(this));
        }

        // updates state after sleeping or main window focusing change to prevent location errors
        document.addEventListener("visibilitychange", function (ev) {
            if (!document.hidden && this.state.run) {
                //doing the request the highAccuracy location request to prevent location error after sleeping
                this._requestHighAccuracyLocation();
            }
        }.bind(this));

        /*
            initiate updates sending workflow
         */
        this.sendUpdateTimerID = setInterval(function () {

            if (! this.state.run)
                return

            let state = this.state;

            //there is no gps data
            if (! state.lastAcceptedData)
                return;

            //it is the inititial locating stage and there is no GPS data in the queue
            if (!state.initialLocatingCompleted && this.gpsDataQueue.length == 0)
                return;

            let timestamp = new Date().getTime();

            //we have a new data to transmit
            if (state.lastAcceptedData != state.lastSendData) {
                state.lastSendData = state.lastAcceptedData;
                if (this.sendUpdate)
                    this.sendUpdate(state.lastAcceptedData);
                state.lastSendTimestamp = timestamp;
                return;
            }

            //there is no recent data for gpsMaxSendInterval seconds, retransmit the last one to signal we are alive
            if (timestamp - state.lastSendTimestamp > this.CONST_gpsMaxSendInterval ) {
                //if accuracy too low for last several measures send it too to notice the server
                if (this.sendUpdate)
                    this.sendUpdate( state.accuracyTooLow ? Object.assign({}, state.lastAcceptedData, {acc: state.accuracy}) : state.lastAcceptedData);
                state.lastSendTimestamp = timestamp;
                return;
            }
        }.bind(this), this.sendInterval)
    }

    /*
        process a new location data
        gpsDataRecord [location] - struct
     */
    store(location) {
        this.stat.set("lastGpsUpdateTimestamp", location.ts);
        this.stat.add("gpsUpdates");
        let last = this.getLastLocation();
        if (last && location.acc == last.acc && location.ts - Math.abs(last.ts) < 1000) {
            //console.warn("GPS updates are too frequent, skipped");
            this.stat.add("gpsUpdatesFiltered");
            return false;
        }
        this._dataQueueStore(this.gpsDataQueue, location);

        //calc avg accuracy and accuracyTooLow flag with last 3 records
        this._calcAccuracyAvg(this.gpsDataQueue);

        //accept if there is no accepted data at all or an accepted data is older 1 day
        if (!this.state.lastAcceptedData || location.ts - this.state.lastAcceptedData > 3600 * 24 * 1000) {
            this._acceptLocationData(location);
            return true;
        }

        //do not accept the location if accuracy is low and our last accepted position has a better accuracy and it is in the surrounding area
        if (location.acc > this.requiredAccuracy &&
            this.distance(location, this.state.lastAcceptedData) < location.acc &&
            location.acc > this.state.lastAcceptedData.acc
        ) {
            this.stat.add("gpsUpdatesFiltered");
            return false;
        }

        //initial locating mode
        if (!this.state.initialLocatingCompleted && location.acc < this.requiredAccuracy) {
            this._acceptLocationData(location);
            return true;
        }

        if (this.distance(location, this.state.lastAcceptedData) > 5 ||
            location.acc < this.state.lastAcceptedData.acc
        ) {
            this._acceptLocationData(location);
            return true;
        }

        this.stat.add("gpsUpdatesFiltered");
        return false;
    }

    getAcceptedLocation() {
        return this.state.lastAcceptedData
    }

    getLastLocation() {
        return this.gpsDataQueue.length > 0 ? this.gpsDataQueue[this.gpsDataQueue.length - 1] : null;
    }

    start() {
        if (! this.state.run) {
            this.startedAt = new Date().getTime();
            this.state.run = true;
            this.fire("start")
        }

        this._requestHighAccuracyLocation();
        this.map.locate({
            setView: false,
            watch: true,
            timeout: this.timeout,
        });
    }

    stop() {
        this.state.run = false;
        this.map.stopLocate();
        this.startedAt = null;
        this.fire("stop")
    }
    /*
        request a HighAccuracy location from browser's geolocation api
        store the received location into the queue and call succ or err callbacks on success or error
     */
    _requestHighAccuracyLocation(succ, err) {

        if (! this.state.run)
            return

        let options = {
            enableHighAccuracy: true,
            timeout: this.timeout,
            maximumAge: 0
        };

        navigator.geolocation.getCurrentPosition(
            function (e) {
                if (e.coords) {
                    this._dataQueueStore(
                        this.gpsHighAccuracyDataQueue,
                        {
                            ts: e.timestamp,
                            lat: e.coords.latitude,
                            lng: e.coords.longitude,
                            acc: e.coords.accuracy,
                            alt: e.coords.altitude,
                            heading: e.coords.heading,
                            speed: e.coords.speed
                        }
                    );
                }
                if (succ)
                    succ(e)
            }.bind(this), function (e) {
                if (err)
                    err(e)
            }.bind(this),
            options
        );
    }

    /*
       update the state to the locationData
     */
    _acceptLocationData(locationData) {
        this.state.lastAcceptedData = locationData;
        this.stat.set("lastGpsUpdateAppliedTimestamp", locationData.ts);
        this.state.located = locationData.acc < this.requiredAccuracy;
        if (! this.state.initialLocatingCompleted && this.state.located )
            this.state.initialLocatingCompleted = true
    }

    /*
    store dataRecord into the queue
     */
    _dataQueueStore(queue, record) {
        let items = queue.length;
        if (items >= this.CONST_gpsDataQueueLength)
            queue.shift();
        if (items > 0) {
            //remove old records (with age > this.gpsDataQueueTimeWindow)
            while(queue[0] && record.ts != null && queue[0].ts != null && (record.ts - queue[0].ts) > this.CONST_gpsDataQueueTimeWindow) {
                queue.shift();
            }
        }
        queue.push(record);
    }

    /*
        calculate an average accuracy for last itemsCount measurements and update accuracy state flags
        array [arr]           array of gpsDataRecord objects
        int   [itemsCount]    count of items to process
     */
    _calcAccuracyAvg(arr, itemsCount) {
        let items = arr.length;
        //calc avg accuracy and accuracyTooLow flag with last 3 records
        let itemsToCheck = itemsCount || 3;
        let avgAccuracy = 0;
        let accuracyTooLow = true;
        let accuracy;
        let lastIdx = items -1;
        if (items < itemsToCheck)
            itemsToCheck = items;

        for( let i = lastIdx; i > lastIdx - itemsToCheck; i--) {
            avgAccuracy += arr[i].acc;
            if (arr[i].acc < this.requiredAccuracy) {
                accuracyTooLow = false;
                accuracy = arr[i].acc;
            }
        }
        this.state.accuracy = accuracyTooLow ? Math.floor(avgAccuracy / itemsToCheck) : accuracy;
        this.state.accuracyTooLow = accuracyTooLow;
    }

    /*
       a dataRecord factory
     */
    newDataRecord(lat, lng, alt, acc, ts) {
        let r = {
            __proto__: gpsDataRecord,
            lat: lat,
            lng: lng,
            alt: alt,
            acc: acc,
            ts: ts || new Date().getTime()
        };
        if (this.gpsDataQueue.length > 0) {
            let lastR = this.gpsDataQueue[this.gpsDataQueue.length - 1];
            r.offset = this.distance(r, lastR);
            let dtime = (r.ts - lastR.ts) / 1000;
            r.speed = r.offset / dtime;
        }
        return r;
    }

    GPSLocationErrorEvent(e) {
        /*
                possible error codes
                    PERMISSION_DENIED:    1
                    POSITION_UNAVAILABLE: 2
                    TIMEOUT:              3
                 */
        this.stat.add("gpsAPIErrorEvents");
        elog("INFO","gpsAPIerror", {code: e.code, message: e.message});

        let curTimestamp = new Date().getTime();

        //save last gpsState
        //this.gpsLastEvent = e

        //show error message if there is timeout error during 120 seconds or any other error
        if (e.code !== 3 || (curTimestamp - this.startedAt > 120 * 1000)) {
            let error = (e.message.indexOf('permission') > -1 || e.message.indexOf('denied') > -1) ? "NO GPS PERMISSION" : "CAN'T GET GPS LOCATION";
            // todo fire event
            this.fire("error", {error: {code: e.code, message: error, detail: e.message}})
        }

        if (e.code === 3) {
            //restart a location watching
            this.start();
        }
    }

    GPSLocationFoundEvent(e) {
        let initialLocatingCompleteStatus = this.state.initialLocatingCompleted;
        let gpsData = this.newDataRecord(e.latitude, e.longitude, null, e.accuracy);
        if (this.store(gpsData)) {
            this.stat.set("lastGpsUpdateAppliedTimestamp", this.state.lastAcceptedData.ts);
            e.offset = this.state.lastAcceptedData.offset;
        }

        //set accuracy value on low accuracy
        if (this.state.accuracyTooLow) {
            e.accuracy = this.state.accuracy;
            e.lowAccuracy = true;
        }


        //this.gpsLastEvent = e;

        // initial locating is completed
        if (!initialLocatingCompleteStatus && this.state.initialLocatingCompleted) {
            this.fire("located", {location: e})
        } else {
            this.fire("location", {location: e})
        }

        /*
        //turn on located status desktop browsers (without a GPS sensor)
        if (!this.state.initialLocatingCompleted &&
            UA &&
            (! UA.isMobileDevice && this.state.accuracyTooLow && new Date().getTime() - this.startedAt > 5 * 1000)) {
            this.fire("located", {location: e})
        }
         */
    }
    /*
        simplified distance calculator
        it's fast and pretty accurate for small offsets
        p1, p2 - struct {lat, lng}
     */
    distance(p1, p2) {
        if (!p1 || !p2 || !p1.lat || !p1.lng || !p2.lat || !p2.lng)
            return 0;
        let dy = (p1.lat - p2.lat) * this.CONST_dgrInMeters;
        let dx = (p1.lng - p2.lng) * this._parralelLengthInMeters(p1.lat);
        return Math.trunc(Math.sqrt(dx * dx + dy * dy));
    }

    /*
    length of geographical parallel for latitude
     */
    _parralelLengthInMeters(degree) {
        let dictIdx = Math.trunc((degree / this.CONST_dictParallelLengthsStep));
        return this.dictParallelLengths[dictIdx];
    }
}

export {gpsStateMachine};
